import { useState } from "react";
import { toast } from "sonner";
import { Loader2, UserCog, ChevronDown } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ConsentBoxes } from "@/components/AuthFields";

const cls = "w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow/70";
const KEYS = ["name", "phone", "company", "vat", "sdi", "address", "city", "zip", "province", "extra_notes"];
const COMPANY = [["company", "Ragione sociale *", "sm:col-span-2", 160], ["vat", "Partita IVA *", "", 16], ["sdi", "Codice SDI *", "", 7]];
const ADDRESS = [["address", "Via e numero civico", "sm:col-span-2", 200], ["city", "Città", "", 100], ["zip", "CAP", "", 5], ["province", "Provincia (es. FC)", "", 2]];
const fmt = (d) => (d ? new Date(d).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Rome" }) : "");

export function MyProfile() {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState(() => ({ ...Object.fromEntries(KEYS.map((k) => [k, user[k] || ""])), is_company: !!user.is_company,
    marketing_consent: !!user.marketing_consent, profiling_consent: !!user.profiling_consent }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/auth/profile", f);
      setUser(data);
      toast.success("Dati aggiornati");
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  const inp = ([k, ph, span, max]) => (
    <input key={k} data-testid={`profile-${k}-input`} className={`${cls} ${span}`} placeholder={ph} maxLength={max} value={f[k]}
      onChange={(e) => setF({ ...f, [k]: k === "sdi" || k === "province" ? e.target.value.toUpperCase() : e.target.value })} />
  );
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="my-profile">
      <button data-testid="my-profile-toggle" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-3 p-5 text-left">
        <span className="flex items-center gap-3"><UserCog className="w-5 h-5 text-brand-navy" /><span><span className="block font-bold uppercase">I miei dati e privacy</span>
          <span className="block text-xs text-slate-500">Dati personali e di fatturazione, consensi marketing e profilazione</span></span></span>
        <ChevronDown className={`w-5 h-5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <form onSubmit={save} className="px-5 pb-5 space-y-4 fade-up" data-testid="my-profile-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="profile-name-input" className={cls} placeholder="Nome e cognome" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
            {inp(["phone", "Telefono", "", 40])}
            <input className={`${cls} bg-slate-50 text-slate-500 sm:col-span-2`} value={user.email} disabled data-testid="profile-email" title="Per cambiare email contattaci" />
          </div>
          <label className="flex items-center gap-3 text-sm font-semibold cursor-pointer">
            <input type="checkbox" data-testid="profile-is-company-checkbox" checked={f.is_company} onChange={(e) => setF({ ...f, is_company: e.target.checked })} className="w-4 h-4 accent-[#0A2E5C]" />
            Sono un'azienda / ho la Partita IVA
          </label>
          <div className="grid sm:grid-cols-2 gap-3">
            {f.is_company && COMPANY.map(inp)}
            {ADDRESS.map(inp)}
            <textarea data-testid="profile-extra-notes-input" rows={2} maxLength={1000} className={`${cls} h-auto py-2 sm:col-span-2`} placeholder="Note: insegna, ufficio, citofono…"
              value={f.extra_notes} onChange={(e) => setF({ ...f, extra_notes: e.target.value })} />
          </div>
          <div>
            <div className="font-mono text-[11px] tracking-widest text-slate-500 mb-2">PRIVACY E CONSENSI</div>
            <ConsentBoxes f={f} setF={setF} withPrivacy={false} />
            <p className="text-[11px] text-slate-500 mt-2" data-testid="profile-consent-dates">
              {user.privacy_accepted_at && <>Informativa accettata il {fmt(user.privacy_accepted_at)}. </>}
              {user.marketing_consent_at && <>Marketing aggiornato il {fmt(user.marketing_consent_at)}. </>}
              {user.profiling_consent_at && <>Profilazione aggiornata il {fmt(user.profiling_consent_at)}.</>}
            </p>
          </div>
          <button data-testid="profile-save-btn" disabled={busy} className="press h-10 px-5 rounded-md bg-brand-yellow text-brand-navy text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Salva i miei dati
          </button>
        </form>
      )}
    </div>
  );
}
